const HIRING_SHORTLIST_STORAGE_PREFIX =
  "maya-hiring-role-shortlists";

export const HIRING_SHORTLIST_SYNC_EVENT =
  "maya-hiring-shortlist-sync";

const getTenantScope = () =>
  sessionStorage.getItem("tenantId") ||
  sessionStorage.getItem("mainTenantId") ||
  "default";

const getStorageKey = () =>
  `${HIRING_SHORTLIST_STORAGE_PREFIX}:${getTenantScope()}`;

export const readHiringShortlists = () => {
  if (typeof window === "undefined") return {};

  try {
    const storedValue = localStorage.getItem(getStorageKey());
    return storedValue ? JSON.parse(storedValue) : {};
  } catch (error) {
    console.error("Unable to read Hiring shortlists:", error);
    return {};
  }
};

export const writeHiringShortlists = (shortlists = {}) => {
  if (typeof window === "undefined") return shortlists;

  localStorage.setItem(
    getStorageKey(),
    JSON.stringify(shortlists),
  );

  window.dispatchEvent(
    new CustomEvent(HIRING_SHORTLIST_SYNC_EVENT, {
      detail: shortlists,
    }),
  );

  return shortlists;
};

export const subscribeHiringShortlists = (callback) => {
  if (typeof window === "undefined") return () => {};

  const sync = () => callback(readHiringShortlists());

  const handleStorage = (event) => {
    if (event.key && event.key !== getStorageKey()) return;
    sync();
  };

  window.addEventListener(HIRING_SHORTLIST_SYNC_EVENT, sync);
  window.addEventListener("storage", handleStorage);

  return () => {
    window.removeEventListener(HIRING_SHORTLIST_SYNC_EVENT, sync);
    window.removeEventListener("storage", handleStorage);
  };
};

const parseShortlistFlag = (value) => {
  if (typeof value === "boolean") return value;

  if (typeof value === "number") {
    if (value === 1) return true;
    if (value === 0) return false;
  }

  if (typeof value === "string") {
    const normalizedValue = value.trim().toLowerCase();

    if (["true", "1", "yes"].includes(normalizedValue)) return true;
    if (["false", "0", "no"].includes(normalizedValue)) return false;
  }

  return undefined;
};

export const getProfileShortlistFlag = (profile) =>
  [
    profile?.isShortlisted,
    profile?.isShortListed,
    profile?.shortlisted,
    profile?.shortListed,
    profile?.shortlist,
    profile?.shotlist,
    profile?.saved,
    profile?.rawProfile?.isShortlisted,
    profile?.rawProfile?.isShortListed,
    profile?.rawProfile?.shortlisted,
    profile?.rawProfile?.shortListed,
    profile?.rawProfile?.shortlist,
    profile?.rawProfile?.shotlist,
    profile?.rawProfile?.saved,
  ]
    .map(parseShortlistFlag)
    .find((value) => typeof value === "boolean");

export const isProfileInRoleShortlist = (
  shortlists,
  roleId,
  profileId,
) =>
  Boolean(
    shortlists?.[roleId]?.profiles?.some(
      (profile) => String(profile.id) === String(profileId),
    ),
  );

export const toggleProfileInRoleShortlist = (role, profile) => {
  if (!role?.id || !profile?.id) {
    return {
      state: readHiringShortlists(),
      added: false,
    };
  }

  const currentState = readHiringShortlists();

  const currentShortlist = currentState[role.id] || {
    role,
    name: `${role.title} Shortlist`,
    profiles: [],
    updatedAt: new Date().toISOString(),
  };

  const alreadyShortlisted = currentShortlist.profiles.some(
    (item) => item.id === profile.id,
  );

  const nextProfiles = alreadyShortlisted
    ? currentShortlist.profiles.filter(
        (item) => item.id !== profile.id,
      )
    : [...currentShortlist.profiles, profile];

  const nextState = {
    ...currentState,
    [role.id]: {
      ...currentShortlist,
      role,
      profiles: nextProfiles,
      updatedAt: new Date().toISOString(),
    },
  };

  return {
    state: writeHiringShortlists(nextState),
    added: !alreadyShortlisted,
  };
};

export const addProfilesToRoleShortlist = (
  role,
  profiles = [],
) => {
  if (!role?.id || !profiles.length) {
    return readHiringShortlists();
  }

  const currentState = readHiringShortlists();

  const currentShortlist = currentState[role.id] || {
    role,
    name: `${role.title} Shortlist`,
    profiles: [],
    updatedAt: new Date().toISOString(),
  };

  const existingProfileIds = new Set(
    currentShortlist.profiles.map((profile) => profile.id),
  );

  const newProfiles = profiles.filter(
    (profile) => !existingProfileIds.has(profile.id),
  );

  const nextState = {
    ...currentState,
    [role.id]: {
      ...currentShortlist,
      role,
      profiles: [
        ...currentShortlist.profiles,
        ...newProfiles,
      ],
      updatedAt: new Date().toISOString(),
    },
  };

  return writeHiringShortlists(nextState);
};

export const removeProfilesFromRoleShortlist = (
  roleId,
  profileIds = [],
) => {
  const currentState = readHiringShortlists();
  const currentShortlist = currentState[roleId];

  if (!currentShortlist) return currentState;

  const nextState = {
    ...currentState,
    [roleId]: {
      ...currentShortlist,
      profiles: currentShortlist.profiles.filter(
        (profile) => !profileIds.includes(profile.id),
      ),
      updatedAt: new Date().toISOString(),
    },
  };

  return writeHiringShortlists(nextState);
};

export const renameRoleShortlist = (roleId, name) => {
  const trimmedName = String(name || "").trim();
  const currentState = readHiringShortlists();

  if (!trimmedName || !currentState[roleId]) {
    return currentState;
  }

  return writeHiringShortlists({
    ...currentState,
    [roleId]: {
      ...currentState[roleId],
      name: trimmedName,
      updatedAt: new Date().toISOString(),
    },
  });
};
